import type { Locale, SearchRecentEntry } from '@hibro/shared';

import type { Translator } from '../../i18n/i18n';
import {
  type SearchProvider,
  type SearchResult,
  SECTION_RECENT,
} from '../search-contract';

/** How many recent picks the header dropdown shows (需求 FR-14 §4.2.1). */
export const RECENT_DISPLAY_LIMIT = 5;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "3 分钟前" for a recorded timestamp, via `Intl.RelativeTimeFormat` so the
 * wording and pluralisation come from the locale rather than our catalog.
 */
export function relativeTime(at: number, now: number, locale: Locale): string {
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const elapsed = Math.max(0, now - at);
  if (elapsed < HOUR) {
    return format.format(-Math.floor(elapsed / MINUTE), 'minute');
  }
  if (elapsed < DAY) {
    return format.format(-Math.floor(elapsed / HOUR), 'hour');
  }
  return format.format(-Math.floor(elapsed / DAY), 'day');
}

/**
 * Look a stored pick up in the providers that could have produced it.
 *
 * Each candidate is asked for its rows under an empty query — for the built-in
 * providers that is their full set, computed from state the renderer already
 * holds. A provider that answers asynchronously cannot back the recent section
 * (there is nothing to render on the first frame), so it contributes nothing
 * until it grows a `resolve(keys)` of its own.
 */
export function createRecentLookup(
  providers: readonly SearchProvider[],
): (providerId: string, key: string) => SearchResult | undefined {
  return (providerId, key) => {
    const source = providers.find((provider) => provider.id === providerId);
    if (!source) {
      return undefined;
    }
    const rows = source.search(
      { raw: '', text: '', terms: [], tab: { kind: 'all' } },
      new AbortController().signal,
    );
    if (rows instanceof Promise) {
      return undefined;
    }
    return rows.find((row) => row.key === key);
  };
}

export interface RecentProviderDeps {
  /** Stored picks, most recent first. */
  entries: readonly SearchRecentEntry[];
  /**
   * The row a stored entry stands for, as its provider describes it *today*.
   * `undefined` drops the entry — see `SearchRecentEntry`'s note on why the
   * store keeps an identity rather than a rendered snapshot.
   */
  lookup: (providerId: string, key: string) => SearchResult | undefined;
  translator: Pick<Translator, 't' | 'locale'>;
  now?: () => number;
}

/**
 * Recently searched-and-opened rows (需求 FR-14 §4.2.1 最近搜索).
 *
 * Prefix-less mode only: command mode shows just the command section. Choosing
 * a row replays the original action — the entry records something the user
 * searched for *and went into*, so going there again is the expected outcome.
 *
 * Ordering is the store's (most recent first) and is preserved through
 * `mergeResults` as a descending score, the same trick the command provider
 * uses to keep `queryCommands`' order.
 */
export function createRecentProvider(
  deps: () => RecentProviderDeps,
): SearchProvider {
  return {
    id: 'recent',
    section: SECTION_RECENT,
    rank: 0,
    search: (query) => {
      const { entries, lookup, translator, now } = deps();
      const at = (now ?? Date.now)();
      const rows: SearchResult[] = [];
      for (const entry of entries) {
        // Live value first, snapshot only as the fallback — a re-read is always
        // more correct, and for the platform's own providers it always works
        // (见 `SearchRecentEntry`).
        const resolved = lookup(entry.providerId, entry.key) ?? entry.snapshot;
        if (!resolved) {
          continue;
        }
        const haystack = resolved.title.toLowerCase();
        if (!query.terms.every((term) => haystack.includes(term))) {
          continue;
        }
        rows.push({
          ...resolved,
          key: `${entry.providerId}:${entry.key}`,
          providerId: 'recent',
          section: SECTION_RECENT,
          // Picking this row must record what it *replays*, not the recent
          // section itself (见 `SearchResult.origin`).
          origin: { providerId: entry.providerId, key: entry.key },
          subtitle: translator.t('workbench.search.recentSubtitle', {
            time: relativeTime(entry.recordedAt, at, translator.locale),
          }),
          score: entries.length - rows.length,
        });
        if (query.limit !== undefined && rows.length >= query.limit) {
          break;
        }
      }
      return rows;
    },
  };
}
